/**
 * Offer / school product section sub-nav — scroll to anchors (sections stay visible).
 * Active tab follows click + IntersectionObserver while scrolling.
 */
const getSectionId = (link) => {
  const href = link.getAttribute('href') || '';
  if (!href.startsWith('#') || href === '#') return '';
  return href.slice(1);
};

const setLinkActive = (links, sectionId) => {
  links.forEach((link) => {
    link.classList.remove('is-active', 'active');
    link.removeAttribute('aria-current');
  });
  if (!sectionId) return;
  links.forEach((link) => {
    if (getSectionId(link) !== sectionId) return;
    link.classList.add('is-active');
    link.setAttribute('aria-current', 'true');
  });
};

const subNavHeight = () => {
  const nav = document.querySelector('.js-offer-sub-nav, .offer-sub-nav');
  if (!(nav instanceof HTMLElement)) return 0;
  return Math.round(nav.getBoundingClientRect().height);
};

const scrollGap = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--scroll-gap').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 32;
};

const adminBarHeight = () => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--admin-bar-height').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isNavHidden = () => document.body.classList.contains('nav-hide');

const headerVisibleHeight = () => {
  // Przy nav-hide header jest poza ekranem (translateY), ale getBoundingClientRect
  // nadal zwraca pełną wysokość — stąd jawny 0 + admin-bar.
  if (isNavHidden()) {
    return Math.round(adminBarHeight());
  }
  const header = document.querySelector('.header, .site-header, [data-sticky-header]');
  if (header instanceof HTMLElement) {
    return Math.round(header.getBoundingClientRect().height);
  }
  return 0;
};

const stickyOffset = () => {
  // Header (+ sticky switcher) — sekcja ma wylądować pod tabami, nie pod nimi.
  return Math.round(headerVisibleHeight() + subNavHeight() + scrollGap());
};

const scrollToSection = (el, { smooth = true } = {}) => {
  if (!(el instanceof HTMLElement) || !el.isConnected) return;
  const top = window.scrollY + el.getBoundingClientRect().top - stickyOffset();
  window.scrollTo({
    top: Math.max(0, top),
    behavior: smooth ? 'smooth' : 'auto',
  });
};

/** Prefer H2 „Szkolenie narciarskie” over section top for #section-szkolenie. */
const resolveScrollTarget = (sectionId, sectionEl) => {
  // Po syncTermContentSections ID siedzi w nowym węźle — zawsze bierz żywy DOM.
  const live = sectionId ? document.getElementById(sectionId) : null;
  const el = live instanceof HTMLElement ? live : sectionEl;
  if (!(el instanceof HTMLElement) || !el.isConnected) return null;
  if (sectionId === 'section-szkolenie') {
    const title = el.querySelector('.offer-training__title, .offer-page__heading');
    if (title instanceof HTMLElement) return title;
  }
  return el;
};

/**
 * Live foreign offers: post_content often lacks Szkolenie tab — inject when section exists.
 * Order: after Szczegóły, before Region (#mapa) when present.
 */
const ensureTrainingNavLink = (nav) => {
  const section = document.getElementById('section-szkolenie');
  if (!(section instanceof HTMLElement)) return;

  const list = getSubNavList(nav);
  if (!list) return;

  const existing = [...list.querySelectorAll('.offer-sub-nav__link')].find(
    (link) => getSectionId(link) === 'section-szkolenie',
  );
  if (existing) {
    if (existing.textContent.trim() === 'Szkolenie narciarskie') {
      existing.textContent = 'Szkolenie';
    }
    return;
  }

  const li = document.createElement('li');
  const a = document.createElement('a');
  a.href = '#section-szkolenie';
  a.className = 'offer-sub-nav__link';
  a.textContent = 'Szkolenie';
  li.appendChild(a);

  const regionLink = [...list.querySelectorAll('.offer-sub-nav__link')].find(
    (link) => getSectionId(link) === 'mapa',
  );
  const regionLi = regionLink?.closest('li');
  if (regionLi) {
    list.insertBefore(li, regionLi);
  } else {
    const detailsLink = [...list.querySelectorAll('.offer-sub-nav__link')].find(
      (link) => getSectionId(link) === 'section-szczegoly',
    );
    const detailsLi = detailsLink?.closest('li');
    if (detailsLi?.nextSibling) {
      list.insertBefore(li, detailsLi.nextSibling);
    } else if (detailsLi) {
      detailsLi.after(li);
    } else {
      list.prepend(li);
    }
  }
};

const pickActiveSectionId = (targetsById, intersectingIds) => {
  if (!intersectingIds.size) return '';

  const focusY = stickyOffset() + 4;
  let chosen = '';

  targetsById.forEach((el, id) => {
    if (!intersectingIds.has(id)) return;
    if (el.getBoundingClientRect().top <= focusY) {
      chosen = id;
    }
  });

  if (chosen) return chosen;

  for (const id of targetsById.keys()) {
    if (intersectingIds.has(id)) return id;
  }

  return '';
};

const pinOffset = () => headerVisibleHeight();

const initStickyBar = (nav) => {
  if (!(nav instanceof HTMLElement) || !nav.parentNode) return;

  let placeholder = nav.previousElementSibling;
  if (!(placeholder instanceof HTMLElement) || !placeholder.classList.contains('offer-sub-nav-placeholder')) {
    placeholder = document.createElement('div');
    placeholder.className = 'offer-sub-nav-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    nav.parentNode.insertBefore(placeholder, nav);
  }

  const isPinned = () => nav.classList.contains('is-stuck') || nav.classList.contains('is-pinned');

  const measureInFlow = () => {
    const rect = nav.getBoundingClientRect();
    const styles = getComputedStyle(nav);
    const mt = Number.parseFloat(styles.marginTop) || 0;
    const mb = Number.parseFloat(styles.marginBottom) || 0;
    return {
      left: Math.round(rect.left),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      flow: Math.round(rect.height + mt + mb),
    };
  };

  const applyPinnedBox = ({ left, width, height, flow }) => {
    nav.style.setProperty('--offer-sub-nav-left', `${left}px`);
    nav.style.setProperty('--offer-sub-nav-width', `${width}px`);
    placeholder.style.height = `${flow}px`;
    document.documentElement.style.setProperty('--sub-nav-height', `${height}px`);
  };

  const onScrollOrResize = () => {
    const offset = pinOffset();
    const pinned = isPinned();
    const ref = pinned ? placeholder : nav;
    const shouldPin = ref.getBoundingClientRect().top <= offset + 1;

    nav.classList.toggle('is-under-hidden-nav', isNavHidden() && (shouldPin || pinned));

    if (shouldPin && !pinned) {
      applyPinnedBox(measureInFlow());
      nav.classList.add('is-stuck', 'is-pinned');
      return;
    }
    if (!shouldPin && pinned) {
      nav.classList.remove('is-stuck', 'is-pinned');
      placeholder.style.height = '0px';
    }
    const box = pinned && shouldPin ? placeholder.getBoundingClientRect() : nav.getBoundingClientRect();
    if (shouldPin) {
      nav.style.setProperty('--offer-sub-nav-left', `${Math.round(box.left)}px`);
      nav.style.setProperty('--offer-sub-nav-width', `${Math.round(box.width)}px`);
    }
    document.documentElement.style.setProperty('--sub-nav-height', `${Math.round(nav.getBoundingClientRect().height)}px`);
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  document.addEventListener('hski:nav-visibility', onScrollOrResize);
  requestAnimationFrame(onScrollOrResize);
};

const HINT_PREV = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m12.5 5-5 5 5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const HINT_NEXT = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m7.5 5 5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const getSubNavList = (nav) => nav.querySelector('.offer-sub-nav__list');

/** Trening na tyczkach — bez strzałek scroll-hint. */
const shouldHideSubNavHints = (nav) => {
  if (nav?.classList?.contains('offer-sub-nav--no-hints')) return true;
  const body = document.body?.classList;
  if (!body) return false;
  return (
    body.contains('page-trening-na-tyczkach')
    || body.contains('page-template-template-school-poles-php')
  );
};

const ensureSubNavScroller = (nav) => {
  const list = getSubNavList(nav);
  if (!list) return null;
  if (list.parentElement?.classList.contains('offer-sub-nav__scroller')) {
    return { scroller: list.parentElement, list };
  }

  const scroller = document.createElement('div');
  scroller.className = 'offer-sub-nav__scroller';
  list.parentNode.insertBefore(scroller, list);
  scroller.appendChild(list);

  if (shouldHideSubNavHints(nav)) {
    return { scroller, list };
  }

  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'offer-sub-nav__hint offer-sub-nav__hint--prev';
  prev.setAttribute('aria-label', 'Przewiń w lewo');
  prev.innerHTML = HINT_PREV;

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'offer-sub-nav__hint offer-sub-nav__hint--next';
  next.setAttribute('aria-label', 'Przewiń w prawo');
  next.innerHTML = HINT_NEXT;

  scroller.append(prev, next);

  const scrollByPage = (dir) => {
    const amount = Math.max(160, Math.round(list.clientWidth * 0.7));
    list.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };
  prev.addEventListener('click', (event) => {
    event.preventDefault();
    scrollByPage(-1);
  });
  next.addEventListener('click', (event) => {
    event.preventDefault();
    scrollByPage(1);
  });

  return { scroller, list };
};

const syncSubNavOverflow = (nav, list) => {
  if (!list) return;
  const max = Math.max(0, list.scrollWidth - list.clientWidth);
  const start = list.scrollLeft > 4;
  const end = list.scrollLeft < max - 4;
  nav.classList.toggle('is-scrollable', max > 4);
  nav.classList.toggle('has-scroll-start', start);
  nav.classList.toggle('has-scroll-end', end);
};

/** Minimalne przesunięcie (px), zanim traktujemy gest jako drag zamiast kliknięcia. */
const SUB_NAV_DRAG_THRESHOLD_PX = 8;

/**
 * Tablet / desktop bez trackpada: przeciągnięcie listy w poziomie.
 * Klik w `<a>` zawsze nawiguje — scroll tylko po wyraźnym dragu > threshold.
 * Na `--no-hints` / tyczkach nie inicjujemy (lista zwykle mieści się bez scrolla).
 */
const initSubNavDragScroll = (list, { enabled = true } = {}) => {
  if (!enabled || !list || list.dataset.dragScrollReady === '1') return;
  list.dataset.dragScrollReady = '1';

  let tracking = false;
  let dragging = false;
  let startX = 0;
  let startScroll = 0;
  let pointerId = null;

  const onPointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button != null && event.button !== 0) return;
    if (list.scrollWidth <= list.clientWidth + 4) return;
    tracking = true;
    dragging = false;
    startX = event.clientX;
    startScroll = list.scrollLeft;
    pointerId = event.pointerId;
  };

  const onPointerMove = (event) => {
    if (!tracking || (pointerId != null && event.pointerId !== pointerId)) return;
    const dx = event.clientX - startX;
    if (!dragging) {
      if (Math.abs(dx) < SUB_NAV_DRAG_THRESHOLD_PX) return;
      dragging = true;
      list.classList.add('is-dragging');
      try {
        list.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
    list.scrollLeft = startScroll - dx;
  };

  const endDrag = (event) => {
    if (!tracking) return;
    if (pointerId != null && event.pointerId !== pointerId) return;
    const wasDragging = dragging;
    tracking = false;
    dragging = false;
    pointerId = null;
    list.classList.remove('is-dragging');
    if (wasDragging) {
      // Blokuj klik w link tylko po realnym dragu.
      const blockClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        list.removeEventListener('click', blockClick, true);
      };
      list.addEventListener('click', blockClick, true);
    }
  };

  list.addEventListener('pointerdown', onPointerDown);
  list.addEventListener('pointermove', onPointerMove);
  list.addEventListener('pointerup', endDrag);
  list.addEventListener('pointercancel', endDrag);
};

const scrollSubNavLinkIntoView = (list, link) => {
  if (!list || !link || !list.contains(link)) return;
  const listBox = list.getBoundingClientRect();
  const linkBox = link.getBoundingClientRect();
  const pad = 44;
  if (linkBox.left < listBox.left + pad) {
    list.scrollBy({ left: linkBox.left - listBox.left - pad, behavior: 'smooth' });
  } else if (linkBox.right > listBox.right - pad) {
    list.scrollBy({ left: linkBox.right - listBox.right + pad, behavior: 'smooth' });
  }
};

const initOfferSubNavRoot = (nav) => {
  ensureTrainingNavLink(nav);
  initStickyBar(nav);

  const scrollerApi = ensureSubNavScroller(nav);

  const list = scrollerApi?.list || getSubNavList(nav);
  const syncOverflow = () => syncSubNavOverflow(nav, list);
  if (list) {
    list.addEventListener('scroll', syncOverflow, { passive: true });
    window.addEventListener('resize', syncOverflow);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(syncOverflow);
      ro.observe(list);
      ro.observe(nav);
    }
    initSubNavDragScroll(list, { enabled: !shouldHideSubNavHints(nav) });
    requestAnimationFrame(syncOverflow);
  }

  const links = [...nav.querySelectorAll('.offer-sub-nav__link')];
  if (!links.length) return;

  /** @type {Map<string, HTMLElement>} */
  const targetsById = new Map();
  /** @type {IntersectionObserver | null} */
  let sectionObserver = null;

  const collectTargets = () => {
    targetsById.clear();
    links.forEach((link) => {
      const id = getSectionId(link);
      if (!id || targetsById.has(id)) return;
      const el = document.getElementById(id);
      if (el) targetsById.set(id, el);
    });
  };

  collectTargets();
  if (!targetsById.size) return;

  let activeId = '';
  let scrollingProgrammatically = false;
  /** @type {Set<string>} */
  const intersectingIds = new Set();

  const activate = (sectionId) => {
    if (!sectionId || sectionId === activeId) return;
    activeId = sectionId;
    setLinkActive(links, sectionId);
    const activeLink = links.find((link) => getSectionId(link) === sectionId);
    scrollSubNavLinkIntoView(list, activeLink);
  };

  const observeSections = () => {
    if (!('IntersectionObserver' in window)) return;
    sectionObserver?.disconnect();
    intersectingIds.clear();
    sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (!id || !targetsById.has(id)) return;
          if (entry.isIntersecting) intersectingIds.add(id);
          else intersectingIds.delete(id);
        });

        if (scrollingProgrammatically) return;

        const nextId = pickActiveSectionId(targetsById, intersectingIds);
        if (nextId) activate(nextId);
      },
      {
        // Węższe „pasmo” pod sticky — mniej nakładających się sekcji naraz.
        rootMargin: `-${stickyOffset()}px 0px -50% 0px`,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );
    targetsById.forEach((el) => sectionObserver.observe(el));
  };

  /** Po podmianie paczek treści (SchoolProduct) — nowe węzły z tymi samymi id. */
  const refreshTargets = () => {
    collectTargets();
    observeSections();
  };
  nav._hskiRefreshSubNavTargets = refreshTargets;

  nav.addEventListener('click', (event) => {
    const link = event.target.closest('.offer-sub-nav__link');
    if (!link || !nav.contains(link)) return;

    const sectionId = getSectionId(link);
    // Żywy DOM — Map z bootu może trzymać odłączony węzeł po syncTermContentSections.
    const target = resolveScrollTarget(sectionId, targetsById.get(sectionId));
    if (!sectionId || !target) return;

    event.preventDefault();
    scrollingProgrammatically = true;
    if (target.id === sectionId) {
      targetsById.set(sectionId, target);
    } else {
      const liveSection = document.getElementById(sectionId);
      if (liveSection) targetsById.set(sectionId, liveSection);
    }
    activate(sectionId);
    scrollToSection(target, { smooth: true });
    if (history.replaceState) {
      history.replaceState(null, '', `#${sectionId}`);
    }
    window.setTimeout(() => {
      scrollingProgrammatically = false;
    }, 700);
  });

  observeSections();

  const hashId = window.location.hash.replace(/^#/, '');
  if (hashId && (targetsById.has(hashId) || document.getElementById(hashId))) {
    activate(hashId);
    requestAnimationFrame(() => {
      scrollToSection(resolveScrollTarget(hashId, targetsById.get(hashId)), { smooth: false });
    });
  } else {
    const firstId = targetsById.keys().next().value;
    if (firstId) activate(firstId);
  }
};

const refreshAllOfferSubNavTargets = () => {
  document.querySelectorAll('.js-offer-sub-nav').forEach((nav) => {
    if (typeof nav._hskiRefreshSubNavTargets === 'function') {
      nav._hskiRefreshSubNavTargets();
    }
  });
};

if (typeof document !== 'undefined') {
  document.addEventListener('hski:term-content-updated', refreshAllOfferSubNavTargets);
}

const initOfferSubNav = () => {
  document.querySelectorAll('.js-offer-sub-nav').forEach((nav) => {
    if (nav.dataset.offerSubNavReady === '1') return;
    nav.dataset.offerSubNavReady = '1';
    initOfferSubNavRoot(nav);
  });
};

export { refreshAllOfferSubNavTargets };
export default initOfferSubNav;
